import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Trash2, RotateCcw, AlertTriangle, CheckCircle, XCircle, Clock, Settings, ArrowLeft } from 'lucide-react';
import { getApiUrl } from '../config';

const QuarantineManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('quarantined');
  const [quarantinedItems, setQuarantinedItems] = useState([]);
  const [threatActions, setThreatActions] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [stats, setStats] = useState({});
  const [protectionLevel, setProtectionLevel] = useState('balanced');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuarantineData();
    loadProtectionLevel();
  }, []);

  const loadQuarantineData = async () => {
    try {
      setLoading(true);

      const API_URL = getApiUrl();

      // Load quarantined items
      const itemsRes = await fetch(`${API_URL}/api/quarantine/items?status=quarantined`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const itemsData = await itemsRes.json();
      setQuarantinedItems(itemsData.items || []);

      // Load threat actions
      const actionsRes = await fetch(`${API_URL}/api/quarantine/actions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const actionsData = await actionsRes.json();
      setThreatActions(actionsData.actions || []);

      // Load pending actions
      const pendingRes = await fetch(`${API_URL}/api/quarantine/pending`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const pendingData = await pendingRes.json();
      setPendingActions(pendingData.pending_actions || []);

      // Load stats
      const statsRes = await fetch(`${API_URL}/api/quarantine/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData.stats || {});

    } catch (error) {
      console.error('Failed to load quarantine data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProtectionLevel = async () => {
    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/api/settings/protection-level`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setProtectionLevel(data.protection_level || 'balanced');
    } catch (error) {
      console.error('Failed to load protection level:', error);
    }
  };

  const handleRestore = async (quarantineId) => {
    if (!confirm('Are you sure you want to restore this file? Make sure it\'s safe first.')) return;

    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/api/quarantine/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ quarantine_id: quarantineId })
      });

      const data = await res.json();
      if (data.success) {
        alert('File restored successfully!');
        loadQuarantineData();
      } else {
        alert(`Failed to restore: ${data.error}`);
      }
    } catch (error) {
      alert('Failed to restore file');
      console.error(error);
    }
  };

  const handleDelete = async (quarantineId) => {
    if (!confirm('Are you sure you want to permanently delete this file? This cannot be undone.')) return;

    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/api/quarantine/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ quarantine_id: quarantineId })
      });

      const data = await res.json();
      if (data.success) {
        alert('File permanently deleted');
        loadQuarantineData();
      } else {
        alert(`Failed to delete: ${data.error}`);
      }
    } catch (error) {
      alert('Failed to delete file');
      console.error(error);
    }
  };

  const handleTakeAction = async (threatId, action, threatType, threatData) => {
    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/api/quarantine/take-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          threat_id: threatId,
          action: action,
          threat_type: threatType,
          threat_data: threatData
        })
      });

      const data = await res.json();
      if (data.success) {
        alert(`Action ${action} completed successfully`);
        loadQuarantineData();
      } else {
        alert(`Failed: ${data.error}`);
      }
    } catch (error) {
      alert('Failed to take action');
      console.error(error);
    }
  };

  const handleProtectionLevelChange = async (level) => {
    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/api/settings/protection-level`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ level })
      });

      const data = await res.json();
      if (data.success) {
        setProtectionLevel(level);
        alert('Protection level updated!');
      }
    } catch (error) {
      alert('Failed to update protection level');
      console.error(error);
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading quarantine data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Threat Quarantine</h1>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>
          <p className="text-gray-600">
            Centralized management of quarantined threats and security actions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Quarantined</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_quarantined || 0}</p>
              </div>
              <Shield className="w-12 h-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Processes Terminated</p>
                <p className="text-3xl font-bold text-gray-900">{stats.processes_terminated || 0}</p>
              </div>
              <XCircle className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Connections Blocked</p>
                <p className="text-3xl font-bold text-gray-900">{stats.connections_blocked || 0}</p>
              </div>
              <AlertTriangle className="w-12 h-12 text-orange-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Review</p>
                <p className="text-3xl font-bold text-gray-900">{stats.pending_user_review || 0}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Pending Actions Alert */}
        {pendingActions.length > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                  {pendingActions.length} Threat{pendingActions.length > 1 ? 's' : ''} Awaiting Your Decision
                </h3>
                <p className="text-yellow-700 mb-4">
                  These threats require your review before action can be taken.
                </p>
                <button
                  onClick={() => setActiveTab('pending')}
                  className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition"
                >
                  Review Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('quarantined')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'quarantined'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quarantined Items ({quarantinedItems.length})
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'pending'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Review ({pendingActions.length})
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'history'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Action History ({threatActions.length})
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'settings'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Settings className="w-4 h-4 inline mr-2" />
                Protection Settings
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Quarantined Items Tab */}
            {activeTab === 'quarantined' && (
              <div>
                {quarantinedItems.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Quarantined Items</h3>
                    <p className="text-gray-600">All threats have been handled. Your system is protected!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {quarantinedItems.map((item) => (
                      <div key={item.quarantine_id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityColor(item.severity)}`}>
                                {item.severity?.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-500">
                                {new Date(item.quarantine_date).toLocaleString()}
                              </span>
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">{item.threat_name}</h4>
                            <p className="text-sm text-gray-600 mb-2">
                              <span className="font-medium">Original Path:</span> {item.original_path}
                            </p>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Confidence:</span> {item.confidence}%
                            </p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleRestore(item.quarantine_id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Restore"
                            >
                              <RotateCcw className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.quarantine_id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete Permanently"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pending Review Tab */}
            {activeTab === 'pending' && (
              <div>
                {pendingActions.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Actions</h3>
                    <p className="text-gray-600">All detected threats have been reviewed.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingActions.map((threat) => (
                      <div key={threat.threat_id} className="border border-yellow-300 bg-yellow-50 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityColor(threat.severity)}`}>
                                {threat.severity?.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-600">
                                Confidence: {threat.confidence}%
                              </span>
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                              {threat.data?.process || threat.data?.name || 'Unknown Threat'}
                            </h4>
                            <p className="text-sm text-gray-700 mb-4">
                              {threat.data?.description || threat.data?.reason || 'Suspicious activity detected'}
                            </p>
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleTakeAction(threat.threat_id, 'quarantine', threat.type, threat.data)}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                              >
                                Quarantine/Block
                              </button>
                              <button
                                onClick={() => handleTakeAction(threat.threat_id, 'ignore', threat.type, threat.data)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                              >
                                Ignore Once
                              </button>
                              <button
                                onClick={() => handleTakeAction(threat.threat_id, 'always_allow', threat.type, threat.data)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                              >
                                Always Allow
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action History Tab */}
            {activeTab === 'history' && (
              <div>
                {threatActions.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Action History</h3>
                    <p className="text-gray-600">Threat actions will appear here once taken.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {threatActions.map((action, idx) => (
                      <div key={idx} className="border-l-4 border-blue-500 bg-gray-50 p-4 rounded-r-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900">
                            {action.action === 'terminated' ? 'Process Terminated' :
                             action.action === 'blocked' ? 'Connection Blocked' :
                             action.action}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(action.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">
                          {action.process_name || action.remote_ip || action.threat_name}
                        </p>
                        <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
                          action.action_type === 'auto' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {action.action_type === 'auto' ? 'Automatic' : 'Manual'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Protection Settings Tab */}
            {activeTab === 'settings' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Protection Level</h3>
                <p className="text-gray-600 mb-6">
                  Choose how CyberGuard responds to threats. Higher protection = more automatic actions.
                </p>

                <div className="space-y-4">
                  {['paranoid', 'balanced', 'hands_off'].map((level) => (
                    <div
                      key={level}
                      onClick={() => handleProtectionLevelChange(level)}
                      className={`border-2 rounded-lg p-6 cursor-pointer transition ${
                        protectionLevel === level
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-semibold text-gray-900 capitalize">
                          {level.replace('_', ' ')}
                          {level === 'balanced' && ' (Recommended)'}
                        </h4>
                        {protectionLevel === level && (
                          <CheckCircle className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {level === 'paranoid' && 'Maximum protection - asks about almost everything (>30% confidence)'}
                        {level === 'balanced' && 'Recommended - auto-handles critical threats (>90% confidence)'}
                        {level === 'hands_off' && 'Minimal interruption - auto-handles most threats (>70% confidence)'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuarantineManager;
